import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import HelpModal from '../../../components/HelpModal/HelpModal'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import CustomerSelectionModal from '../../../components/CustomerSelectionModal/CustomerSelectionModal'
import projectBillingService from '../../../services/projectBillingService'
import projectService from '../../../services/projectService'
import customerMasterService from '../../../services/customerMasterService'
import paymentTermsMasterService from '../../../services/paymentTermsMasterService'
import timesheetService from '../../../services/timesheetService'
import projectExpenseService from '../../../services/projectExpenseService'
import milestoneService from '../../../services/milestoneService'
import { 
  Save, 
  X, 
  Plus, 
  Trash2, 
  FileText,
  DollarSign,
  Calendar,
  AlertCircle,
  RotateCcw,
  Clock,
  Receipt,
  CheckCircle2,
  HelpCircle
} from 'lucide-react'

const ProjectBillingForm = ({ mode = 'create', billingId = null, initialData = null, breadcrumbItems: customBreadcrumbItems = null }) => {
  const navigate = useNavigate()
  const { id } = useParams()
  const actualBillingId = billingId || id
  const actualMode = mode || (actualBillingId ? (window.location.pathname.includes('/edit/') ? 'edit' : 'view') : 'create')
  
  const [showHelp, setShowHelp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [billingDataLoading, setBillingDataLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')
  const [masterDataLoaded, setMasterDataLoaded] = useState(false)
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState({ isOpen: false, index: null })
  
  const isEditMode = actualMode === 'edit'
  const isViewMode = actualMode === 'view'
  const isReadOnly = isViewMode
  
  const [masterData, setMasterData] = useState({
    projects: [],
    customers: [],
    paymentTerms: [],
    timesheets: [],
    expenses: [],
    milestones: []
  })

  const [formData, setFormData] = useState({
    // Basic Information
    billingNumber: '',
    project: '',
    projectNumber: '',
    projectName: '',
    customer: '',
    customerCode: '',
    customerName: '',
    customerGSTIN: '',
    customerCategory: 'Domestic',
    billingDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    billingPeriod: {
      startDate: '',
      endDate: ''
    },
    
    // Billing Items
    billingItems: [],
    
    // Financial Summary
    subtotal: 0,
    discount: 0,
    discountType: 'Fixed',
    taxAmount: 0,
    totalAmount: 0,
    currency: 'INR',
    paymentTerms: '',
    
    // Payment Information
    paymentStatus: 'Unpaid',
    paidAmount: 0,
    outstandingAmount: 0,
    
    // Invoice Information
    invoiceNumber: '',
    invoiceDate: '',
    invoiceGenerated: false,
    
    // Status
    status: 'Draft',
    
    // Additional Information
    notes: '',
    remarks: ''
  })

  const [errors, setErrors] = useState({})
  const [newBillingItem, setNewBillingItem] = useState({
    itemType: 'Timesheet',
    itemReference: '',
    itemReferenceModel: 'Timesheet',
    description: '',
    quantity: 1,
    unitRate: 0,
    amount: 0,
    taxRate: 0,
    taxAmount: 0,
    totalAmount: 0,
    currency: 'INR'
  })

  // Load master data
  useEffect(() => {
    if (!masterDataLoaded) {
      loadMasterData()
    }
  }, [])

  // Load billing data for edit/view mode
  useEffect(() => {
    if ((isEditMode || isViewMode) && actualBillingId && masterDataLoaded) {
      if (initialData) {
        populateFormData(initialData)
      } else {
        loadBillingData()
      }
    } else if (!isEditMode && !isViewMode) {
      // Set default due date (30 days from billing date)
      const billingDate = new Date(formData.billingDate)
      const dueDate = new Date(billingDate)
      dueDate.setDate(dueDate.getDate() + 30)
      setFormData(prev => ({
        ...prev,
        dueDate: dueDate.toISOString().split('T')[0]
      }))
    }
  }, [actualBillingId, isEditMode, isViewMode, masterDataLoaded])

  const loadMasterData = async () => {
    try {
      setMasterDataLoaded(false)
      const [
        projectsRes,
        customersRes,
        paymentTermsRes
      ] = await Promise.allSettled([
        projectService.getAllProjects({ limit: 1000 }),
        customerMasterService.getAllCustomers({ limit: 1000 }),
        paymentTermsMasterService.getActivePaymentTerms()
      ])

      const getProjectsData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.projects) {
            return res.projects
          }
          if (Array.isArray(res)) {
            return res
          }
        }
        return []
      }

      const getCustomersData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
          if (Array.isArray(res)) {
            return res
          }
        }
        return []
      }

      const getPaymentTermsData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
          if (Array.isArray(res)) {
            return res
          }
        }
        return []
      }

      setMasterData(prev => ({
        ...prev,
        projects: getProjectsData(projectsRes),
        customers: getCustomersData(customersRes),
        paymentTerms: getPaymentTermsData(paymentTermsRes)
      }))
      
      setMasterDataLoaded(true)
    } catch (error) {
      console.error('Error loading master data:', error)
      toast.error('Failed to load master data')
      setMasterDataLoaded(true) // Set to true even on error to prevent infinite loop
    }
  }

  const loadBillingData = async () => {
    try {
      setBillingDataLoading(true)
      const response = await projectBillingService.getProjectBillingById(actualBillingId)
      
      if (response.success && response.data) {
        populateFormData(response.data)
      } else {
        toast.error(response.message || 'Failed to load project billing')
        navigate('/projects/billings')
      }
    } catch (error) {
      console.error('Error loading project billing:', error)
      toast.error('Failed to load project billing')
      navigate('/projects/billings')
    } finally {
      setBillingDataLoading(false)
    }
  }

  const populateFormData = (data) => {
    setFormData({
      billingNumber: data.billingNumber || '',
      project: data.project?._id || data.project || '',
      projectNumber: data.projectNumber || data.project?.projectNumber || '',
      projectName: data.projectName || data.project?.projectName || '',
      customer: data.customer?._id || data.customer || '',
      customerCode: data.customerCode || data.customer?.customerCode || '',
      customerName: data.customerName || data.customer?.customerName || '',
      customerGSTIN: data.customerGSTIN || data.customer?.GSTIN || '',
      customerCategory: data.customerCategory || 'Domestic',
      billingDate: data.billingDate ? new Date(data.billingDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      dueDate: data.dueDate ? new Date(data.dueDate).toISOString().split('T')[0] : '',
      billingPeriod: data.billingPeriod || { startDate: '', endDate: '' },
      billingItems: data.billingItems || [],
      subtotal: data.subtotal || 0,
      discount: data.discount || 0,
      discountType: data.discountType || 'Fixed',
      taxAmount: data.taxAmount || 0,
      totalAmount: data.totalAmount || 0,
      currency: data.currency || 'INR',
      paymentTerms: data.paymentTerms || '',
      paymentStatus: data.paymentStatus || 'Unpaid',
      paidAmount: data.paidAmount || 0,
      outstandingAmount: data.outstandingAmount || 0,
      invoiceNumber: data.invoiceNumber || '',
      invoiceDate: data.invoiceDate ? new Date(data.invoiceDate).toISOString().split('T')[0] : '',
      invoiceGenerated: data.invoiceGenerated || false,
      status: data.status || 'Draft',
      notes: data.notes || '',
      remarks: data.remarks || ''
    })
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }

    // Auto-calculate due date when billing date changes
    if (name === 'billingDate' && value) {
      const billingDate = new Date(value)
      const dueDate = new Date(billingDate)
      dueDate.setDate(dueDate.getDate() + 30)
      setFormData(prev => ({
        ...prev,
        billingDate: value,
        dueDate: dueDate.toISOString().split('T')[0]
      }))
    }

    // Update project details when project changes
    if (name === 'project' && value) {
      const selectedProject = masterData.projects.find(p => (p._id || p.id) === value)
      if (selectedProject) {
        setFormData(prev => ({
          ...prev,
          project: value,
          projectNumber: selectedProject.projectNumber || '',
          projectName: selectedProject.projectName || '',
          // Get customer from project if available
          customer: selectedProject.customer?._id || selectedProject.customer || prev.customer
        }))
        
        // Load customer details if project has customer
        if (selectedProject.customer) {
          loadCustomerDetails(selectedProject.customer._id || selectedProject.customer)
        }
      }
    }
  }

  const loadCustomerDetails = async (customerId) => {
    try {
      const response = await customerMasterService.getCustomerById(customerId)
      if (response && response.success && response.data) {
        const customer = response.data
        setFormData(prev => ({
          ...prev,
          customer: customerId,
          customerCode: customer.customerCode || '',
          customerName: customer.customerName || '',
          customerGSTIN: customer.GSTIN || '',
          customerCategory: customer.customerCategory || 'Domestic',
          paymentTerms: customer.customerPaymentTerms || prev.paymentTerms
        }))
      }
    } catch (error) {
      console.error('Error loading customer details:', error)
    }
  }

  const handleCustomerSelect = (customer) => {
    setFormData(prev => ({
      ...prev,
      customer: customer._id || customer.id,
      customerCode: customer.customerCode || customer.code,
      customerName: customer.customerName || customer.name,
      customerGSTIN: customer.GSTIN || customer.gstin || '',
      customerCategory: customer.customerCategory || customer.category || 'Domestic',
      paymentTerms: customer.customerPaymentTerms || customer.paymentTerms || prev.paymentTerms
    }))
    setShowCustomerModal(false)
  }

  const handleNestedInputChange = (parentKey, childKey, value) => {
    setFormData(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: value
      }
    }))
  }

  const handleBillingItemChange = (field, value) => {
    setNewBillingItem(prev => {
      const updated = { ...prev, [field]: value }
      
      // Calculate amount and total
      if (field === 'quantity' || field === 'unitRate') {
        updated.amount = (updated.quantity || 0) * (updated.unitRate || 0)
      }
      
      if (field === 'amount' || field === 'taxRate') {
        updated.taxAmount = (updated.amount || 0) * ((updated.taxRate || 0) / 100)
      }
      
      updated.totalAmount = (updated.amount || 0) + (updated.taxAmount || 0)
      
      return updated
    })
  }

  const addBillingItem = () => {
    if (!newBillingItem.description || newBillingItem.amount <= 0) {
      toast.error('Please provide description and amount for the billing item')
      return
    }

    setFormData(prev => ({
      ...prev,
      billingItems: [...prev.billingItems, { ...newBillingItem }]
    }))

    // Reset new billing item
    setNewBillingItem({
      itemType: 'Timesheet',
      itemReference: '',
      itemReferenceModel: 'Timesheet',
      description: '',
      quantity: 1,
      unitRate: 0,
      amount: 0,
      taxRate: 0,
      taxAmount: 0,
      totalAmount: 0,
      currency: 'INR'
    })

    calculateTotals()
    toast.success('Billing item added successfully')
  }

  const removeBillingItem = (index) => {
    setShowDeleteModal({ isOpen: true, index })
  }

  const confirmRemoveItem = () => {
    if (showDeleteModal.index !== null) {
      setFormData(prev => ({
        ...prev,
        billingItems: prev.billingItems.filter((_, i) => i !== showDeleteModal.index)
      }))
      calculateTotals()
      toast.success('Billing item removed successfully')
      setShowDeleteModal({ isOpen: false, index: null })
    }
  }

  const calculateTotals = () => {
    setFormData(prev => {
      const subtotal = prev.billingItems.reduce((sum, item) => sum + (item.amount || 0), 0)
      const taxAmount = prev.billingItems.reduce((sum, item) => sum + (item.taxAmount || 0), 0)
      
      let finalSubtotal = subtotal
      if (prev.discount && prev.discount > 0) {
        if (prev.discountType === 'Percentage') {
          finalSubtotal = subtotal * (1 - prev.discount / 100)
        } else {
          finalSubtotal = subtotal - prev.discount
        }
      }
      
      const totalAmount = finalSubtotal + taxAmount
      const outstandingAmount = Math.max(0, totalAmount - (prev.paidAmount || 0))
      
      return {
        ...prev,
        subtotal: finalSubtotal,
        taxAmount,
        totalAmount,
        outstandingAmount
      }
    })
  }

  useEffect(() => {
    calculateTotals()
  }, [formData.billingItems, formData.discount, formData.discountType, formData.paidAmount])

  const validateForm = () => {
    const newErrors = {}

    if (!formData.project) {
      newErrors.project = 'Project is required'
    }
    if (!formData.customer) {
      newErrors.customer = 'Customer is required'
    }
    if (!formData.billingDate) {
      newErrors.billingDate = 'Billing date is required'
    }
    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required'
    }
    if (formData.billingItems.length === 0) {
      newErrors.billingItems = 'At least one billing item is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      const errorCount = Object.keys(errors).length
      toast.error(`Please fix ${errorCount} error(s) before submitting`)
      return
    }

    try {
      setLoading(true)
      let response
      
      const billingData = {
        project: formData.project,
        projectNumber: formData.projectNumber,
        projectName: formData.projectName,
        customer: formData.customer,
        customerCode: formData.customerCode,
        customerName: formData.customerName,
        customerGSTIN: formData.customerGSTIN,
        customerCategory: formData.customerCategory,
        billingDate: formData.billingDate,
        dueDate: formData.dueDate,
        billingPeriod: formData.billingPeriod,
        billingItems: formData.billingItems,
        subtotal: formData.subtotal,
        discount: formData.discount,
        discountType: formData.discountType,
        taxAmount: formData.taxAmount,
        totalAmount: formData.totalAmount,
        currency: formData.currency,
        paymentTerms: formData.paymentTerms,
        paymentStatus: formData.paymentStatus,
        paidAmount: formData.paidAmount,
        invoiceNumber: formData.invoiceNumber,
        invoiceDate: formData.invoiceDate || null,
        invoiceGenerated: formData.invoiceGenerated,
        notes: formData.notes,
        remarks: formData.remarks,
        status: formData.status
      }

      if (isEditMode) {
        response = await projectBillingService.updateProjectBilling(actualBillingId, billingData)
        if (response.success) {
          toast.success('Project billing updated successfully')
          navigate('/projects/billings')
        } else {
          toast.error(response.message || 'Failed to update project billing')
        }
      } else {
        response = await projectBillingService.createProjectBilling(billingData)
        if (response.success) {
          toast.success('Project billing created successfully')
          navigate('/projects/billings')
        } else {
          toast.error(response.message || 'Failed to create project billing')
        }
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} project billing:`, error)
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} project billing`)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      billingNumber: '',
      project: '',
      projectNumber: '',
      projectName: '',
      customer: '',
      customerCode: '',
      customerName: '',
      customerGSTIN: '',
      customerCategory: 'Domestic',
      billingDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      billingPeriod: { startDate: '', endDate: '' },
      billingItems: [],
      subtotal: 0,
      discount: 0,
      discountType: 'Fixed',
      taxAmount: 0,
      totalAmount: 0,
      currency: 'INR',
      paymentTerms: '',
      paymentStatus: 'Unpaid',
      paidAmount: 0,
      outstandingAmount: 0,
      invoiceNumber: '',
      invoiceDate: '',
      invoiceGenerated: false,
      status: 'Draft',
      notes: '',
      remarks: ''
    })
    setErrors({})
  }

  const handleTestData = () => {
    const today = new Date()
    const billingDate = today.toISOString().split('T')[0]
    const dueDate = new Date(today)
    dueDate.setDate(dueDate.getDate() + 30)
    
    const testProject = masterData.projects.length > 0 ? masterData.projects[0] : null
    const testCustomer = masterData.customers.length > 0 ? masterData.customers[0] : null
    const testPaymentTerms = masterData.paymentTerms.length > 0 ? masterData.paymentTerms[0].description : 'Net 30'

    const testBillingItems = [
      {
        itemType: 'Timesheet',
        itemReference: '',
        itemReferenceModel: 'Timesheet',
        description: 'Development hours - 40 hours @ ₹1,500/hour',
        quantity: 40,
        unitRate: 1500,
        amount: 60000,
        taxRate: 18,
        taxAmount: 10800,
        totalAmount: 70800,
        currency: 'INR'
      },
      {
        itemType: 'Expense',
        itemReference: '',
        itemReferenceModel: 'ProjectExpense',
        description: 'Travel and accommodation expenses',
        quantity: 1,
        unitRate: 15000,
        amount: 15000,
        taxRate: 18,
        taxAmount: 2700,
        totalAmount: 17700,
        currency: 'INR'
      },
      {
        itemType: 'Milestone',
        itemReference: '',
        itemReferenceModel: 'Milestone',
        description: 'Phase 1 Completion - Milestone payment',
        quantity: 1,
        unitRate: 50000,
        amount: 50000,
        taxRate: 18,
        taxAmount: 9000,
        totalAmount: 59000,
        currency: 'INR'
      }
    ]

    const subtotal = testBillingItems.reduce((sum, item) => sum + item.amount, 0)
    const taxAmount = testBillingItems.reduce((sum, item) => sum + item.taxAmount, 0)
    const totalAmount = subtotal + taxAmount

    setFormData(prev => ({
      ...prev,
      project: testProject ? (testProject._id || testProject.id) : '',
      projectNumber: testProject?.projectNumber || '',
      projectName: testProject?.projectName || 'Test Project',
      customer: testCustomer ? (testCustomer._id || testCustomer.id) : '',
      customerCode: testCustomer?.customerCode || '',
      customerName: testCustomer?.customerName || 'Test Customer',
      customerGSTIN: testCustomer?.GSTIN || '',
      customerCategory: testCustomer?.customerCategory || 'Domestic',
      billingDate: billingDate,
      dueDate: dueDate.toISOString().split('T')[0],
      billingPeriod: {
        startDate: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0],
        endDate: new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0]
      },
      billingItems: testBillingItems,
      subtotal: subtotal,
      discount: 0,
      discountType: 'Fixed',
      taxAmount: taxAmount,
      totalAmount: totalAmount,
      currency: 'INR',
      paymentTerms: testPaymentTerms,
      paymentStatus: 'Unpaid',
      paidAmount: 0,
      outstandingAmount: totalAmount,
      status: 'Draft',
      notes: 'Test billing data for demonstration purposes',
      remarks: 'This is a test billing entry'
    }))
    
    setErrors({})
    toast.success('Test data loaded successfully!')
  }

  // Listen for F1 key to populate test data (only in create mode)
  useEffect(() => {
    if (isEditMode || isViewMode) return // Don't add listener in edit/view mode

    const handleKeyPress = (event) => {
      // Check if F1 key is pressed (keyCode 112 or key === 'F1')
      if (event.key === 'F1' || event.keyCode === 112) {
        event.preventDefault() // Prevent browser help menu
        handleTestData()
      }
    }

    // Add event listener
    window.addEventListener('keydown', handleKeyPress)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [isEditMode, isViewMode, masterData]) // Include dependencies used by handleTestData

  const breadcrumbItems = customBreadcrumbItems || [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Project Billing', path: '/projects/billings', onClick: () => navigate('/projects/billings') },
    { label: isEditMode ? 'Edit Billing' : isViewMode ? 'View Billing' : 'Create New', path: isEditMode ? `/projects/billings/edit/${actualBillingId}` : isViewMode ? `/projects/billings/view/${actualBillingId}` : '/projects/billings/new', isCurrent: true }
  ]

  const tabs = [
    { id: 'basic', label: 'Basic Information', icon: FileText },
    { id: 'items', label: 'Billing Items', icon: Receipt },
    { id: 'financial', label: 'Financial Summary', icon: DollarSign },
    { id: 'notes', label: 'Notes & Remarks', icon: FileText }
  ]

  const helpContent = (
    <>
      <h3>What is Project Billing Entry?</h3>
      <p>Project Billing Entry lets you <strong>create and manage billing events and revenue recognition</strong> for projects. You link billing to a project and customer, add billing items (time, expenses, milestones), and track amounts and status.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Information</strong> – Billing number, project, customer, billing date, due date, status, payment terms.</p>
      <p><strong>Billing Items</strong> – Line items (description, type, quantity, rate, amount); link to timesheets, expenses, or milestones.</p>
      <p><strong>Financial Summary</strong> – Subtotal, tax, total, and revenue recognition.</p>
      <p><strong>Notes &amp; Remarks</strong> – Internal notes and remarks.</p>
      <h3>How to use</h3>
      <p>Select <strong>project</strong> and <strong>customer</strong>, add billing items. Use <strong>Save</strong> to create or update. Use the list to view all project billings.</p>
    </>
  )

  if (billingDataLoading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading billing data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Project Billing' : isViewMode ? 'View Project Billing' : 'Create Project Billing'}</h1>
          <p>{isEditMode ? 'Update project billing information' : isViewMode ? 'View project billing details' : 'Create and manage project billings'}</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Project Billing Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''} ${isViewMode ? 'cursor-pointer' : ''}`}
                onClick={() => !isReadOnly || isViewMode ? setActiveTab(tab.id) : undefined}
                disabled={!isReadOnly && false}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </div>
          
        <form className="form-content" onSubmit={handleSubmit}>
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Basic Information</h3>
              </div>
              
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="billingNumber" className="form-label">
                    Billing #
                  </label>
                  <input
                    type="text"
                    id="billingNumber"
                    name="billingNumber"
                    value={formData.billingNumber || 'Auto-generated on save'}
                    readOnly
                    className="form-input"
                    placeholder="Auto-generated on save"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed', fontStyle: 'italic' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="project" className="form-label required">
                    Project
                  </label>
                  <select
                    id="project"
                    name="project"
                    value={formData.project}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                    required
                  >
                    <option value="">Select Project</option>
                    {masterData.projects.map(project => (
                      <option key={project._id || project.id} value={project._id || project.id}>
                        {project.projectNumber} - {project.projectName}
                      </option>
                    ))}
                  </select>
                  {errors.project && (
                    <p className="form-error">{errors.project}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="billingDate" className="form-label required">
                    Billing Date
                  </label>
                  <input
                    type="date"
                    id="billingDate"
                    name="billingDate"
                    value={formData.billingDate}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                    required
                  />
                  {errors.billingDate && (
                    <p className="form-error">{errors.billingDate}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="dueDate" className="form-label required">
                    Due Date
                  </label>
                  <input
                    type="date"
                    id="dueDate"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                    required
                  />
                  {errors.dueDate && (
                    <p className="form-error">{errors.dueDate}</p>
                  )}
                </div>
              </div>

              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="customer" className="form-label required">
                    Customer
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      id="customerName"
                      name="customerName"
                      value={formData.customerName || 'Select customer'}
                      readOnly
                      className="form-input"
                      placeholder="Select customer"
                      style={{ flex: 1, backgroundColor: '#f9fafb', cursor: 'pointer' }}
                      onClick={() => !isReadOnly && setShowCustomerModal(true)}
                    />
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={() => setShowCustomerModal(true)}
                        className="btn-secondary"
                        style={{ padding: '8px 12px' }}
                      >
                        <X size={16} style={{ transform: 'rotate(45deg)' }} />
                      </button>
                    )}
                  </div>
                  {errors.customer && (
                    <p className="form-error">{errors.customer}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="customerCode" className="form-label">
                    Customer Code
                  </label>
                  <input
                    type="text"
                    id="customerCode"
                    name="customerCode"
                    value={formData.customerCode}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#f9fafb' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerCategory" className="form-label">
                    Customer Category
                  </label>
                  <select
                    id="customerCategory"
                    name="customerCategory"
                    value={formData.customerCategory}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                  >
                    <option value="Domestic">Domestic</option>
                    <option value="Export">Export</option>
                    <option value="SEZ">SEZ</option>
                    <option value="Deemed Export">Deemed Export</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="currency" className="form-label required">
                    Currency
                  </label>
                  <select
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                    required
                  >
                    <option value="INR">INR - Indian Rupee</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                  </select>
                </div>
              </div>

              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="paymentTerms" className="form-label">
                    Payment Terms
                  </label>
                  <select
                    id="paymentTerms"
                    name="paymentTerms"
                    value={formData.paymentTerms}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                  >
                    <option value="">Select Payment Terms</option>
                    {masterData.paymentTerms.map(term => (
                      <option key={term._id || term.id} value={term.description}>
                        {term.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="status" className="form-label">
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                  >
                    <option value="Draft">Draft</option>
                    <option value="Pending Approval">Pending Approval</option>
                    <option value="Approved">Approved</option>
                    <option value="Invoiced">Invoiced</option>
                    <option value="Sent">Sent</option>
                    <option value="Paid">Paid</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="billingPeriodStart" className="form-label">
                    Billing Period Start
                  </label>
                  <input
                    type="date"
                    id="billingPeriodStart"
                    name="billingPeriodStart"
                    value={formData.billingPeriod.startDate}
                    onChange={(e) => handleNestedInputChange('billingPeriod', 'startDate', e.target.value)}
                    className="form-input"
                    disabled={isReadOnly}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="billingPeriodEnd" className="form-label">
                    Billing Period End
                  </label>
                  <input
                    type="date"
                    id="billingPeriodEnd"
                    name="billingPeriodEnd"
                    value={formData.billingPeriod.endDate}
                    onChange={(e) => handleNestedInputChange('billingPeriod', 'endDate', e.target.value)}
                    className="form-input"
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Billing Items Tab */}
          {activeTab === 'items' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Billing Items</h3>
              </div>

              {!isReadOnly && (
                <div className="grid-row-4" style={{ marginBottom: '20px', padding: '16px', background: '#f9fafb', borderRadius: '4px' }}>
                  <div className="form-group">
                    <label className="form-label">Item Type</label>
                    <select
                      value={newBillingItem.itemType}
                      onChange={(e) => {
                        const itemType = e.target.value
                        const itemReferenceModel = itemType === 'Timesheet' ? 'Timesheet' : 
                                                  itemType === 'Expense' ? 'ProjectExpense' : 
                                                  itemType === 'Milestone' ? 'Milestone' : 'Project'
                        handleBillingItemChange('itemType', itemType)
                        handleBillingItemChange('itemReferenceModel', itemReferenceModel)
                      }}
                      className="form-input"
                    >
                      <option value="Timesheet">Timesheet</option>
                      <option value="Expense">Expense</option>
                      <option value="Milestone">Milestone</option>
                      <option value="Fixed">Fixed</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <input
                      type="text"
                      value={newBillingItem.description}
                      onChange={(e) => handleBillingItemChange('description', e.target.value)}
                      className="form-input"
                      placeholder="Enter item description"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Quantity</label>
                    <input
                      type="number"
                      value={newBillingItem.quantity}
                      onChange={(e) => handleBillingItemChange('quantity', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Unit Rate</label>
                    <input
                      type="number"
                      value={newBillingItem.unitRate}
                      onChange={(e) => handleBillingItemChange('unitRate', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Tax Rate (%)</label>
                    <input
                      type="number"
                      value={newBillingItem.taxRate}
                      onChange={(e) => handleBillingItemChange('taxRate', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Amount</label>
                    <input
                      type="number"
                      value={newBillingItem.amount}
                      readOnly
                      className="form-input"
                      style={{ backgroundColor: '#f9fafb' }}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Tax Amount</label>
                    <input
                      type="number"
                      value={newBillingItem.taxAmount}
                      readOnly
                      className="form-input"
                      style={{ backgroundColor: '#f9fafb' }}
                    />
                  </div>

                  <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={addBillingItem}
                      className="btn-primary"
                      style={{ width: '100%' }}
                    >
                      <Plus size={16} />
                      Add Item
                    </button>
                  </div>
                </div>
              )}

              {formData.billingItems.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '16px' }}>
                    <thead>
                      <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Item Type</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Description</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Quantity</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Unit Rate</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Amount</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Tax Rate</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Tax Amount</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Total</th>
                        {!isReadOnly && (
                          <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Action</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {formData.billingItems.map((item, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px' }}>{item.itemType}</td>
                          <td style={{ padding: '12px' }}>{item.description}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{item.quantity}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{item.currency === 'INR' ? '₹' : item.currency} {item.unitRate.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{item.currency === 'INR' ? '₹' : item.currency} {item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{item.taxRate}%</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{item.currency === 'INR' ? '₹' : item.currency} {item.taxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>{item.currency === 'INR' ? '₹' : item.currency} {item.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          {!isReadOnly && (
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <button
                                type="button"
                                onClick={() => removeBillingItem(index)}
                                className="btn-secondary"
                                style={{ padding: '4px 8px' }}
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                  <Receipt size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                  <p>No billing items added yet</p>
                  {!isReadOnly && (
                    <p style={{ fontSize: '12px', marginTop: '8px' }}>Add items using the form above</p>
                  )}
                </div>
              )}
              {errors.billingItems && (
                <p className="form-error" style={{ marginTop: '12px' }}>{errors.billingItems}</p>
              )}
            </div>
          )}

          {/* Financial Summary Tab */}
          {activeTab === 'financial' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Financial Summary</h3>
              </div>

              <div className="grid-row-4">
                <div className="form-group">
                  <label className="form-label">Subtotal</label>
                  <input
                    type="text"
                    value={`${formData.currency === 'INR' ? '₹' : formData.currency} ${formData.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#f9fafb', fontWeight: 600 }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Discount Type</label>
                  <select
                    value={formData.discountType}
                    onChange={(e) => handleInputChange({ target: { name: 'discountType', value: e.target.value } })}
                    className="form-input"
                    disabled={isReadOnly}
                  >
                    <option value="Fixed">Fixed Amount</option>
                    <option value="Percentage">Percentage</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Discount</label>
                  <input
                    type="number"
                    name="discount"
                    value={formData.discount}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Tax Amount</label>
                  <input
                    type="text"
                    value={`${formData.currency === 'INR' ? '₹' : formData.currency} ${formData.taxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#f9fafb', fontWeight: 600 }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Total Amount</label>
                  <input
                    type="text"
                    value={`${formData.currency === 'INR' ? '₹' : formData.currency} ${formData.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#E8E1F5', fontWeight: 700, fontSize: '16px', color: '#51418F' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Payment Status</label>
                  <select
                    name="paymentStatus"
                    value={formData.paymentStatus}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                  >
                    <option value="Unpaid">Unpaid</option>
                    <option value="Partially Paid">Partially Paid</option>
                    <option value="Paid">Paid</option>
                    <option value="Overdue">Overdue</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Paid Amount</label>
                  <input
                    type="number"
                    name="paidAmount"
                    value={formData.paidAmount}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Outstanding Amount</label>
                  <input
                    type="text"
                    value={`${formData.currency === 'INR' ? '₹' : formData.currency} ${formData.outstandingAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#fef3c7', fontWeight: 600 }}
                  />
                </div>
              </div>

              <div className="grid-row-4" style={{ marginTop: '20px' }}>
                <div className="form-group">
                  <label className="form-label">Invoice Number</label>
                  <input
                    type="text"
                    name="invoiceNumber"
                    value={formData.invoiceNumber}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                    placeholder="Auto-generated when invoice is created"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Invoice Date</label>
                  <input
                    type="date"
                    name="invoiceDate"
                    value={formData.invoiceDate}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isReadOnly}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.invoiceGenerated}
                      onChange={(e) => handleInputChange({ target: { name: 'invoiceGenerated', value: e.target.checked } })}
                      disabled={isReadOnly}
                    />
                    Invoice Generated
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Notes & Remarks Tab */}
          {activeTab === 'notes' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Notes & Remarks</h3>
              </div>

              <div className="form-group">
                <label htmlFor="notes" className="form-label">
                  Notes
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="form-input"
                  rows="5"
                  disabled={isReadOnly}
                  placeholder="Enter any additional notes or comments..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="remarks" className="form-label">
                  Remarks
                </label>
                <textarea
                  id="remarks"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleInputChange}
                  className="form-input"
                  rows="5"
                  disabled={isReadOnly}
                  placeholder="Enter any remarks or special instructions..."
                />
              </div>
            </div>
          )}

          {/* Form Actions */}
          {!isViewMode && (
            <div className="form-actions" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                {!isEditMode && (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleReset}
                  >
                    <RotateCcw size={16} />
                    Reset
                  </button>
                )}
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => navigate('/projects/billings')}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                  ) : (
                    <span>{isEditMode ? 'Update Billing' : 'Create Billing'}</span>
                  )}
                </button>
              </div>
            </div>
          )}
          {isViewMode && (
            <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate('/projects/billings')}
              >
                Back
              </button>
            </div>
          )}
        </form>
      </div>

      {/* Customer Selection Modal */}
      <CustomerSelectionModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onSelectCustomer={handleCustomerSelect}
        includeProspects={false}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal.isOpen}
        onClose={() => setShowDeleteModal({ isOpen: false, index: null })}
        onConfirm={confirmRemoveItem}
        title="Remove Billing Item"
        message="Are you sure you want to remove this billing item?"
        confirmText="Remove"
        cancelText="Cancel"
        type="danger"
      />

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Project Billing Entry – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default ProjectBillingForm

